export type DomainId =
  | 'identity'
  | 'newsletter'
  | 'article'
  | 'reading'
  | 'challenge'
  | 'native'
  | 'blog'
  | 'engagement'
  | 'ops';

export type FieldBadge = 'PK' | 'FK' | 'NN' | 'UQ' | 'ENUM' | 'IDX';

export interface ErdField {
  name: string;
  type: string;
  badges?: FieldBadge[];
  relation?: string;
}

export interface ErdEntity {
  id: string;
  table: string;
  entity: string;
  domain: DomainId;
  source: string;
  summary: string;
  x: number;
  y: number;
  fields: ErdField[];
  uniques?: string[];
  featured?: boolean;
}

export interface ErdRelationship {
  from: string;
  to: string;
  field: string;
  label: string;
  strength: 'constraint' | 'inferred';
}

export type ErdIndexType = 'index' | 'unique' | 'fulltext';

export interface ErdIndex {
  name: string;
  type: ErdIndexType;
  columns: string[];
  source: string;
}

export const domainMeta: Record<
  DomainId,
  {
    label: string;
    color: string;
    softColor: string;
  }
> = {
  identity: {
    label: '멤버',
    color: '#2563EB',
    softColor: '#E8F0FF',
  },
  newsletter: {
    label: '뉴스레터',
    color: '#EA580C',
    softColor: '#FFF0E7',
  },
  article: {
    label: '아티클',
    color: '#0891B2',
    softColor: '#E4F7FB',
  },
  reading: {
    label: '읽기',
    color: '#16A34A',
    softColor: '#E9F8EF',
  },
  challenge: {
    label: '챌린지',
    color: '#7C3AED',
    softColor: '#F1EAFE',
  },
  native: {
    label: '매일메일',
    color: '#C026D3',
    softColor: '#FBE9FD',
  },
  blog: {
    label: '블로그',
    color: '#DB2777',
    softColor: '#FDEAF3',
  },
  engagement: {
    label: '성장',
    color: '#CA8A04',
    softColor: '#FFF7D6',
  },
  ops: {
    label: '운영',
    color: '#475569',
    softColor: '#EDF1F5',
  },
};

export const erdEntities: ErdEntity[] = [
  {
    id: 'role',
    table: 'role',
    entity: 'Role',
    domain: 'identity',
    source: 'member/domain/Role.java',
    summary: '권한 기준 데이터',
    x: 80,
    y: 96,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'authority', type: 'String', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'member',
    table: 'member',
    entity: 'Member',
    domain: 'identity',
    source: 'member/domain/Member.java',
    summary: 'OAuth 회원 프로필과 권한',
    x: 360,
    y: 96,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'provider', type: 'String', badges: ['NN', 'UQ'] },
      { name: 'providerId', type: 'String', badges: ['NN', 'UQ'] },
      { name: 'email', type: 'String', badges: ['NN'] },
      { name: 'nickname', type: 'String', badges: ['NN', 'UQ'] },
      {
        name: 'roleId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'role.id',
      },
      { name: 'gender', type: 'Gender', badges: ['ENUM', 'NN'] },
    ],
    uniques: ['provider + providerId', 'nickname'],
    featured: true,
  },
  {
    id: 'warning_setting',
    table: 'warning_setting',
    entity: 'WarningSetting',
    domain: 'identity',
    source: 'member/domain/WarningSetting.java',
    summary: '회원 알림 설정',
    x: 640,
    y: 72,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN', 'UQ'],
        relation: 'member.id',
      },
      { name: 'isEnabled', type: 'boolean', badges: ['NN'] },
    ],
  },
  {
    id: 'withdrawn_member',
    table: 'withdrawn_member',
    entity: 'WithdrawnMember',
    domain: 'identity',
    source: 'withdraw/domain/WithdrawnMember.java',
    summary: '탈퇴 회원 스냅샷',
    x: 640,
    y: 288,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'memberId', type: 'Long', badges: ['FK'] },
      { name: 'provider', type: 'String', badges: ['NN'] },
      { name: 'email', type: 'String', badges: ['NN'] },
      { name: 'withdrawnAt', type: 'LocalDateTime' },
    ],
  },
  {
    id: 'coupon_issue',
    table: 'coupon_issue',
    entity: 'CouponIssue',
    domain: 'engagement',
    source: 'coupon/domain/CouponIssue.java',
    summary: '회원별 쿠폰 발급',
    x: 80,
    y: 328,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      { name: 'couponType', type: 'String', badges: ['NN'] },
      { name: 'issuedAt', type: 'LocalDateTime' },
    ],
  },
  {
    id: 'stage',
    table: 'stage',
    entity: 'Stage',
    domain: 'engagement',
    source: 'pet/domain/Stage.java',
    summary: '펫 성장 단계 기준',
    x: 640,
    y: 520,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'level', type: 'int', badges: ['NN'] },
      { name: 'requiredScore', type: 'int', badges: ['NN'] },
    ],
  },
  {
    id: 'pet',
    table: 'pet',
    entity: 'Pet',
    domain: 'engagement',
    source: 'pet/domain/Pet.java',
    summary: '회원별 펫 성장 상태',
    x: 360,
    y: 520,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'stageId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'stage.id',
      },
      { name: 'currentScore', type: 'int', badges: ['NN'] },
      { name: 'isAttended', type: 'boolean', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'badge',
    table: 'badge',
    entity: 'Badge',
    domain: 'engagement',
    source: 'badge/domain/Badge.java',
    summary: '회원 획득 배지',
    x: 80,
    y: 544,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      { name: 'badgeCategory', type: 'BadgeCategory', badges: ['ENUM', 'NN'] },
    ],
    featured: true,
  },
  {
    id: 'category',
    table: 'category',
    entity: 'Category',
    domain: 'newsletter',
    source: 'newsletter/domain/Category.java',
    summary: '뉴스레터 카테고리',
    x: 920,
    y: 96,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'name', type: 'String', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'newsletter_detail',
    table: 'newsletter_detail',
    entity: 'NewsletterDetail',
    domain: 'newsletter',
    source: 'newsletter/domain/NewsletterDetail.java',
    summary: '구독 URL과 발행 정보',
    x: 1200,
    y: 96,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'mainPageUrl', type: 'String', badges: ['NN'] },
      { name: 'subscribeUrl', type: 'String', badges: ['NN'] },
      { name: 'issueCycle', type: 'String', badges: ['NN'] },
      { name: 'subscriptionMethod', type: 'String' },
    ],
    featured: true,
  },
  {
    id: 'newsletter',
    table: 'newsletter',
    entity: 'Newsletter',
    domain: 'newsletter',
    source: 'newsletter/domain/Newsletter.java',
    summary: '외부/네이티브 뉴스레터 마스터',
    x: 1060,
    y: 320,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'name', type: 'String', badges: ['NN'] },
      {
        name: 'categoryId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'category.id',
      },
      {
        name: 'detailId',
        type: 'Long',
        badges: ['FK', 'NN', 'UQ'],
        relation: 'newsletter_detail.id',
      },
      {
        name: 'status',
        type: 'NewsletterPublicationStatus',
        badges: ['ENUM', 'NN'],
      },
      { name: 'source', type: 'NewsletterSource', badges: ['ENUM', 'NN'] },
    ],
    uniques: ['detailId'],
    featured: true,
  },
  {
    id: 'newsletter_previous_policy',
    table: 'newsletter_previous_policy',
    entity: 'NewsletterPreviousPolicy',
    domain: 'newsletter',
    source: 'newsletter/domain/NewsletterPreviousPolicy.java',
    summary: '지난 뉴스레터 수집 정책',
    x: 780,
    y: 536,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
      {
        name: 'strategy',
        type: 'NewsletterPreviousStrategy',
        badges: ['ENUM', 'NN'],
      },
      { name: 'active', type: 'boolean', badges: ['NN'] },
    ],
  },
  {
    id: 'newsletter_group',
    table: 'newsletter_group',
    entity: 'NewsletterGroup',
    domain: 'newsletter',
    source: 'newsletter/domain/NewsletterGroup.java',
    summary: '챌린지용 뉴스레터 묶음',
    x: 1840,
    y: 96,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'name', type: 'String', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'newsletter_group_item',
    table: 'newsletter_group_item',
    entity: 'NewsletterGroupItem',
    domain: 'newsletter',
    source: 'newsletter/domain/NewsletterGroupItem.java',
    summary: '뉴스레터 그룹 구성 항목',
    x: 1840,
    y: 312,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'newsletterGroupId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter_group.id',
      },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
    ],
    uniques: ['newsletterGroupId + newsletterId'],
    featured: true,
  },
  {
    id: 'subscribe',
    table: 'subscribe',
    entity: 'Subscribe',
    domain: 'newsletter',
    source: 'subscribe/domain/Subscribe.java',
    summary: '회원 뉴스레터 구독 상태',
    x: 780,
    y: 320,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      { name: 'status', type: 'SubscribeStatus', badges: ['ENUM', 'NN'] },
      { name: 'unsubscribedAt', type: 'LocalDateTime' },
    ],
    uniques: ['newsletterId + memberId'],
    featured: true,
  },
  {
    id: 'newsletter_subscription_count',
    table: 'newsletter_subscription_count',
    entity: 'NewsletterSubscriptionCount',
    domain: 'newsletter',
    source: 'subscribe/domain/NewsletterSubscriptionCount.java',
    summary: '구독 집계 캐시',
    x: 780,
    y: 752,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'newsletterName', type: 'String', badges: ['NN'] },
      { name: 'ageGroup', type: 'AgeGroup', badges: ['ENUM'] },
      { name: 'subscribeCount', type: 'long', badges: ['NN'] },
    ],
  },
  {
    id: 'unsubscribe_retry',
    table: 'unsubscribe_retry',
    entity: 'UnsubscribeRetry',
    domain: 'newsletter',
    source: 'subscribe/domain/UnsubscribeRetry.java',
    summary: '구독 해지 재시도 큐',
    x: 500,
    y: 752,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'subscribeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'subscribe.id',
      },
      { name: 'retryCount', type: 'int', badges: ['NN'] },
      { name: 'lastErrorMessage', type: 'String' },
    ],
  },
  {
    id: 'article',
    table: 'article',
    entity: 'Article',
    domain: 'article',
    source: 'article/domain/Article.java',
    summary: '회원에게 도착한 뉴스레터 본문',
    x: 1340,
    y: 320,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
      { name: 'title', type: 'String', badges: ['NN'] },
      { name: 'arrivedDateTime', type: 'LocalDateTime', badges: ['NN', 'IDX'] },
      { name: 'contents', type: 'String', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'bookmark',
    table: 'bookmark',
    entity: 'Bookmark',
    domain: 'article',
    source: 'bookmark/domain/Bookmark.java',
    summary: '회원별 아티클 저장',
    x: 1620,
    y: 224,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'articleId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'article.id',
      },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
    ],
    uniques: ['memberId + articleId'],
    featured: true,
  },
  {
    id: 'highlight',
    table: 'highlight',
    entity: 'Highlight',
    domain: 'article',
    source: 'highlight/domain/Highlight.java',
    summary: '아티클 본문 하이라이트와 메모',
    x: 1620,
    y: 440,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'articleId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'article.id',
      },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
      { name: 'color', type: 'Color', badges: ['ENUM', 'NN'] },
      { name: 'memo', type: 'String' },
    ],
    featured: true,
  },
  {
    id: 'article_read_history',
    table: 'article_read_history',
    entity: 'ArticleReadHistory',
    domain: 'article',
    source: 'article/domain/ArticleReadHistory.java',
    summary: '읽음 처리 이력',
    x: 1340,
    y: 560,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'articleId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'article.id',
      },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
      {
        name: 'categoryId',
        type: 'Long',
        badges: ['FK'],
        relation: 'category.id',
      },
    ],
    featured: true,
  },
  {
    id: 'recent_article',
    table: 'recent_article',
    entity: 'RecentArticle',
    domain: 'article',
    source: 'article/domain/RecentArticle.java',
    summary: '최근 아티클 검색/노출 모델',
    x: 1060,
    y: 560,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'articleId',
        type: 'Long',
        badges: ['FK'],
        relation: 'article.id',
      },
      { name: 'memberId', type: 'Long', badges: ['FK'], relation: 'member.id' },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK'],
        relation: 'newsletter.id',
      },
      { name: 'title', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'previous_article',
    table: 'previous_article',
    entity: 'PreviousArticle',
    domain: 'article',
    source: 'article/domain/PreviousArticle.java',
    summary: '지난 뉴스레터 아티클',
    x: 1340,
    y: 776,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
      { name: 'title', type: 'String', badges: ['NN'] },
      { name: 'url', type: 'String', badges: ['NN'] },
      { name: 'publishedAt', type: 'LocalDate' },
    ],
  },
  {
    id: 'today_reading',
    table: 'today_reading',
    entity: 'TodayReading',
    domain: 'reading',
    source: 'reading/domain/TodayReading.java',
    summary: '일간 읽기 현황',
    x: 80,
    y: 864,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN', 'UQ'],
        relation: 'member.id',
      },
      { name: 'currentCount', type: 'int', badges: ['NN'] },
      { name: 'totalCount', type: 'int', badges: ['NN'] },
      { name: 'readCount', type: 'int' },
    ],
    featured: true,
  },
  {
    id: 'weekly_reading',
    table: 'weekly_reading',
    entity: 'WeeklyReading',
    domain: 'reading',
    source: 'reading/domain/WeeklyReading.java',
    summary: '주간 읽기 목표',
    x: 360,
    y: 864,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN', 'UQ'],
        relation: 'member.id',
      },
      { name: 'currentCount', type: 'int', badges: ['NN'] },
      { name: 'goalCount', type: 'int', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'monthly_reading_realtime',
    table: 'monthly_reading_realtime',
    entity: 'MonthlyReadingRealtime',
    domain: 'reading',
    source: 'reading/domain/MonthlyReadingRealtime.java',
    summary: '월간 읽기 실시간 카운터',
    x: 640,
    y: 864,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN', 'UQ'],
        relation: 'member.id',
      },
      { name: 'currentCount', type: 'int', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'yearly_reading',
    table: 'yearly_reading',
    entity: 'YearlyReading',
    domain: 'reading',
    source: 'reading/domain/YearlyReading.java',
    summary: '연간 읽기 집계',
    x: 920,
    y: 864,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      { name: 'readingYear', type: 'int', badges: ['NN'] },
      { name: 'currentCount', type: 'int', badges: ['NN'] },
    ],
    uniques: ['memberId + readingYear'],
  },
  {
    id: 'continue_reading_realtime',
    table: 'continue_reading_realtime',
    entity: 'ContinueReadingRealtime',
    domain: 'reading',
    source: 'reading/domain/ContinueReadingRealtime.java',
    summary: '연속 읽기 실시간 상태',
    x: 80,
    y: 1080,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN', 'UQ'],
        relation: 'member.id',
      },
      { name: 'dayCount', type: 'int', badges: ['NN'] },
      { name: 'maxDayCount', type: 'int' },
    ],
    featured: true,
  },
  {
    id: 'continue_reading_shield',
    table: 'continue_reading_shield',
    entity: 'ContinueReadingShield',
    domain: 'reading',
    source: 'reading/domain/ContinueReadingShield.java',
    summary: '연속 읽기 보호막 잔여량',
    x: 360,
    y: 1080,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN', 'UQ'],
        relation: 'member.id',
      },
      { name: 'monthlyRemainingCount', type: 'int', badges: ['NN'] },
      { name: 'rewardRemainingCount', type: 'int', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'continue_reading_shield_history',
    table: 'continue_reading_shield_history',
    entity: 'ContinueReadingShieldHistory',
    domain: 'reading',
    source: 'reading/domain/ContinueReadingShieldHistory.java',
    summary: '보호막 지급/차감 이력',
    x: 640,
    y: 1080,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'historyType',
        type: 'ContinueReadingShieldHistoryType',
        badges: ['ENUM', 'NN'],
      },
      {
        name: 'reason',
        type: 'ContinueReadingShieldHistoryReason',
        badges: ['ENUM'],
      },
      { name: 'eventDate', type: 'LocalDate', badges: ['NN'] },
    ],
  },
  {
    id: 'monthly_reading_snapshot',
    table: 'monthly_reading_snapshot',
    entity: 'MonthlyReadingSnapshot',
    domain: 'reading',
    source: 'reading/domain/MonthlyReadingSnapshot.java',
    summary: '월간 랭킹 스냅샷',
    x: 1200,
    y: 864,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'memberId', type: 'Long', badges: ['FK'], relation: 'member.id' },
      { name: 'rank', type: 'int', badges: ['NN'] },
      { name: 'currentCount', type: 'int', badges: ['NN'] },
      { name: 'snapshotDate', type: 'LocalDate', badges: ['IDX'] },
    ],
  },
  {
    id: 'continue_reading_snapshot',
    table: 'continue_reading_snapshot',
    entity: 'ContinueReadingSnapshot',
    domain: 'reading',
    source: 'reading/domain/ContinueReadingSnapshot.java',
    summary: '연속 읽기 랭킹 스냅샷',
    x: 1480,
    y: 864,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'memberId', type: 'Long', badges: ['FK'], relation: 'member.id' },
      { name: 'rank', type: 'int', badges: ['NN'] },
      { name: 'dayCount', type: 'int', badges: ['NN'] },
    ],
  },
  {
    id: 'reading_snapshot_meta',
    table: 'reading_snapshot_meta',
    entity: 'ReadingSnapshotMeta',
    domain: 'reading',
    source: 'reading/domain/ReadingSnapshotMeta.java',
    summary: '랭킹 스냅샷 생성 메타',
    x: 1480,
    y: 1080,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'snapshotType',
        type: 'ReadingSnapshotType',
        badges: ['ENUM', 'NN'],
      },
    ],
  },
  {
    id: 'monthly_reading_rank_history',
    table: 'monthly_reading_rank_history',
    entity: 'MonthlyReadingRankHistory',
    domain: 'reading',
    source: 'reading/domain/MonthlyReadingRankHistory.java',
    summary: '월간 랭킹 히스토리',
    x: 920,
    y: 1080,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'memberId', type: 'Long', badges: ['FK'], relation: 'member.id' },
      { name: 'rank', type: 'int', badges: ['NN'] },
      { name: 'rankedAt', type: 'LocalDateTime', badges: ['IDX'] },
    ],
  },
  {
    id: 'continue_reading_rank_history',
    table: 'continue_reading_rank_history',
    entity: 'ContinueReadingRankHistory',
    domain: 'reading',
    source: 'reading/domain/ContinueReadingRankHistory.java',
    summary: '연속 읽기 랭킹 히스토리',
    x: 1200,
    y: 1080,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'memberId', type: 'Long', badges: ['FK'], relation: 'member.id' },
      { name: 'rank', type: 'int', badges: ['NN'] },
      { name: 'rankedAt', type: 'LocalDateTime', badges: ['IDX'] },
    ],
  },
  {
    id: 'challenge',
    table: 'challenge',
    entity: 'Challenge',
    domain: 'challenge',
    source: 'challenge/domain/Challenge.java',
    summary: '뉴스레터 읽기 챌린지 회차',
    x: 1840,
    y: 536,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'name', type: 'String', badges: ['NN'] },
      { name: 'generation', type: 'int', badges: ['NN'] },
      {
        name: 'newsletterGroupId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter_group.id',
      },
      { name: 'startDate', type: 'LocalDate' },
      { name: 'endDate', type: 'LocalDate' },
    ],
    featured: true,
  },
  {
    id: 'challenge_team',
    table: 'challenge_team',
    entity: 'ChallengeTeam',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeTeam.java',
    summary: '챌린지 팀',
    x: 1840,
    y: 752,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      { name: 'progress', type: 'int', badges: ['NN'] },
    ],
    featured: true,
  },
  {
    id: 'challenge_participant',
    table: 'challenge_participant',
    entity: 'ChallengeParticipant',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeParticipant.java',
    summary: '회원의 챌린지 참여 상태',
    x: 1560,
    y: 752,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'challengeTeamId',
        type: 'Long',
        badges: ['FK'],
        relation: 'challenge_team.id',
      },
      { name: 'streak', type: 'int', badges: ['NN'] },
      { name: 'grade', type: 'ChallengeGrade', badges: ['ENUM'] },
    ],
    uniques: ['challengeId + memberId'],
    featured: true,
  },
  {
    id: 'challenge_todo',
    table: 'challenge_todo',
    entity: 'ChallengeTodo',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeTodo.java',
    summary: '챌린지 할 일 정의',
    x: 1840,
    y: 968,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      { name: 'todoType', type: 'ChallengeTodoType', badges: ['ENUM', 'NN'] },
    ],
    featured: true,
  },
  {
    id: 'challenge_daily_todo',
    table: 'challenge_daily_todo',
    entity: 'ChallengeDailyTodo',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeDailyTodo.java',
    summary: '참여자별 일일 할 일 상태',
    x: 1560,
    y: 968,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'participantId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_participant.id',
      },
      {
        name: 'challengeTodoId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_todo.id',
      },
      { name: 'status', type: 'ChallengeTodoStatus', badges: ['ENUM', 'NN'] },
    ],
    featured: true,
  },
  {
    id: 'challenge_daily_result',
    table: 'challenge_daily_result',
    entity: 'ChallengeDailyResult',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeDailyResult.java',
    summary: '참여자 일일 성공 여부',
    x: 1280,
    y: 1184,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'participantId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_participant.id',
      },
      { name: 'date', type: 'LocalDate', badges: ['NN'] },
      { name: 'status', type: 'ChallengeDailyStatus', badges: ['ENUM', 'NN'] },
    ],
    uniques: ['participantId + date'],
    featured: true,
  },
  {
    id: 'challenge_daily_guide',
    table: 'challenge_daily_guide',
    entity: 'ChallengeDailyGuide',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeDailyGuide.java',
    summary: '챌린지 일자별 가이드',
    x: 1840,
    y: 1184,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      { name: 'guideDate', type: 'LocalDate', badges: ['NN'] },
      { name: 'type', type: 'DailyGuideType', badges: ['ENUM', 'NN'] },
      { name: 'content', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'challenge_daily_guide_comment',
    table: 'challenge_daily_guide_comment',
    entity: 'ChallengeDailyGuideComment',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeDailyGuideComment.java',
    summary: '일일 가이드 댓글',
    x: 1560,
    y: 1184,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'guideId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_daily_guide.id',
      },
      {
        name: 'participantId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_participant.id',
      },
      { name: 'content', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'challenge_comment',
    table: 'challenge_comment',
    entity: 'ChallengeComment',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeComment.java',
    summary: '챌린지 뉴스레터 댓글',
    x: 1280,
    y: 752,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'newsletterId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'newsletter.id',
      },
      {
        name: 'participantId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_participant.id',
      },
      { name: 'content', type: 'String', badges: ['NN'] },
      { name: 'likeCount', type: 'int', badges: ['NN'] },
      { name: 'replyCount', type: 'int', badges: ['NN'] },
    ],
  },
  {
    id: 'challenge_comment_reply',
    table: 'challenge_comment_reply',
    entity: 'ChallengeCommentReply',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeCommentReply.java',
    summary: '챌린지 댓글 답글',
    x: 1280,
    y: 968,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'participantId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_participant.id',
      },
      {
        name: 'commentId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_comment.id',
      },
      { name: 'content', type: 'String', badges: ['NN'] },
      { name: 'isPrivate', type: 'boolean', badges: ['NN'] },
    ],
  },
  {
    id: 'challenge_comment_like',
    table: 'challenge_comment_like',
    entity: 'ChallengeCommentLike',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeCommentLike.java',
    summary: '챌린지 댓글 좋아요',
    x: 1000,
    y: 1184,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'participantId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_participant.id',
      },
      {
        name: 'commentId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge_comment.id',
      },
    ],
    uniques: ['participantId + commentId'],
  },
  {
    id: 'challenge_review',
    table: 'challenge_review',
    entity: 'ChallengeReview',
    domain: 'challenge',
    source: 'challenge/domain/ChallengeReview.java',
    summary: '챌린지 후기',
    x: 1000,
    y: 752,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      { name: 'content', type: 'String', badges: ['NN'] },
      { name: 'isVisible', type: 'boolean', badges: ['NN'] },
    ],
    uniques: ['challengeId + memberId'],
  },
  {
    id: 'challenge_start_notification',
    table: 'challenge_start_notification',
    entity: 'ChallengeStartNotification',
    domain: 'challenge',
    source: 'challenge/domain/notification/ChallengeStartNotification.java',
    summary: '챌린지 시작 알림 발송',
    x: 2120,
    y: 752,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      { name: 'status', type: 'NotificationStatus', badges: ['ENUM', 'NN'] },
    ],
  },
  {
    id: 'challenge_todo_reminder_notification',
    table: 'challenge_todo_reminder_notification',
    entity: 'ChallengeTodoReminderNotification',
    domain: 'challenge',
    source:
      'challenge/domain/notification/ChallengeTodoReminderNotification.java',
    summary: '챌린지 할 일 리마인더',
    x: 2120,
    y: 968,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      { name: 'phase', type: 'ChallengeTodoReminderPhase', badges: ['ENUM'] },
      { name: 'status', type: 'NotificationStatus', badges: ['ENUM', 'NN'] },
    ],
  },
  {
    id: 'challenge_badge',
    table: 'challenge_badge',
    entity: 'ChallengeBadge',
    domain: 'engagement',
    source: 'badge/domain/ChallengeBadge.java',
    summary: '챌린지 달성 배지 기준',
    x: 2120,
    y: 536,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'challengeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'challenge.id',
      },
      { name: 'grade', type: 'ChallengeGrade', badges: ['ENUM', 'NN'] },
      { name: 'name', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'blog_category',
    table: 'blog_category',
    entity: 'BlogCategory',
    domain: 'blog',
    source: 'blog/domain/BlogCategory.java',
    summary: '블로그 카테고리',
    x: 80,
    y: 1480,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'name', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'blog_post',
    table: 'blog_post',
    entity: 'BlogPost',
    domain: 'blog',
    source: 'blog/domain/BlogPost.java',
    summary: '봄봄 블로그 게시글',
    x: 360,
    y: 1480,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'thumbnailImageId',
        type: 'Long',
        badges: ['FK'],
        relation: 'blog_image_asset.id',
      },
      {
        name: 'categoryId',
        type: 'Long',
        badges: ['FK'],
        relation: 'blog_category.id',
      },
      { name: 'status', type: 'BlogPostStatus', badges: ['ENUM', 'NN'] },
      {
        name: 'visibility',
        type: 'BlogPostVisibility',
        badges: ['ENUM', 'NN'],
      },
    ],
  },
  {
    id: 'blog_image_asset',
    table: 'blog_image_asset',
    entity: 'BlogImageAsset',
    domain: 'blog',
    source: 'blog/domain/BlogImageAsset.java',
    summary: '블로그 이미지 자산',
    x: 640,
    y: 1480,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'blogPostId',
        type: 'Long',
        badges: ['FK'],
        relation: 'blog_post.id',
      },
      { name: 'url', type: 'String', badges: ['NN'] },
      { name: 'status', type: 'BlogImageAssetStatus', badges: ['ENUM', 'NN'] },
    ],
  },
  {
    id: 'blog_hashtag',
    table: 'blog_hashtag',
    entity: 'BlogHashtag',
    domain: 'blog',
    source: 'blog/domain/BlogHashtag.java',
    summary: '블로그 해시태그',
    x: 80,
    y: 1696,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'name', type: 'String', badges: ['NN', 'UQ'] },
    ],
  },
  {
    id: 'blog_post_tag',
    table: 'blog_post_tag',
    entity: 'BlogPostTag',
    domain: 'blog',
    source: 'blog/domain/BlogPostTag.java',
    summary: '게시글-해시태그 매핑',
    x: 360,
    y: 1696,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'blogPostId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'blog_post.id',
      },
      {
        name: 'blogHashtagId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'blog_hashtag.id',
      },
    ],
    uniques: ['blogPostId + blogHashtagId'],
  },
  {
    id: 'maeil_mail_topic',
    table: 'maeil_mail_topic',
    entity: 'MaeilMailTopic',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailTopic.java',
    summary: '매일메일 주제',
    x: 920,
    y: 1480,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'track', type: 'MaeilMailTrack', badges: ['ENUM', 'NN'] },
      { name: 'name', type: 'String', badges: ['NN'] },
      { name: 'displayOrder', type: 'int', badges: ['NN'] },
    ],
  },
  {
    id: 'maeil_mail_content',
    table: 'maeil_mail_content',
    entity: 'MaeilMailContent',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailContent.java',
    summary: '매일메일 문제 콘텐츠',
    x: 1200,
    y: 1480,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'topicId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'maeil_mail_topic.id',
      },
      { name: 'title', type: 'String', badges: ['NN'] },
      { name: 'question', type: 'String', badges: ['NN'] },
      { name: 'explanation', type: 'String' },
    ],
  },
  {
    id: 'maeil_mail_content_answer',
    table: 'maeil_mail_content_answer',
    entity: 'MaeilMailContentAnswer',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailContentAnswer.java',
    summary: '매일메일 정답 후보',
    x: 1480,
    y: 1480,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'contentId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'maeil_mail_content.id',
      },
      { name: 'answer', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'maeil_mail_issue_history',
    table: 'maeil_mail_issue_history',
    entity: 'MaeilMailIssueHistory',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailIssueHistory.java',
    summary: '아티클별 매일메일 발행 이력',
    x: 1200,
    y: 1696,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'articleId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'article.id',
      },
      {
        name: 'contentId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'maeil_mail_content.id',
      },
    ],
    uniques: ['articleId + contentId'],
  },
  {
    id: 'maeil_mail_sent_content',
    table: 'maeil_mail_sent_content',
    entity: 'MaeilMailSentContent',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailSentContent.java',
    summary: '회원에게 발송된 매일메일 콘텐츠',
    x: 920,
    y: 1696,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      {
        name: 'topicId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'maeil_mail_topic.id',
      },
      {
        name: 'contentId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'maeil_mail_content.id',
      },
    ],
    uniques: ['memberId + topicId + contentId'],
  },
  {
    id: 'maeil_mail_subscription_track',
    table: 'maeil_mail_subscription_track',
    entity: 'MaeilMailSubscriptionTrack',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailSubscriptionTrack.java',
    summary: '구독자의 매일메일 트랙',
    x: 1760,
    y: 1480,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'subscribeId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'subscribe.id',
      },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      { name: 'track', type: 'MaeilMailTrack', badges: ['ENUM', 'NN'] },
      { name: 'active', type: 'boolean', badges: ['NN'] },
    ],
    uniques: ['subscribeId + track'],
  },
  {
    id: 'maeil_mail_user_answer',
    table: 'maeil_mail_user_answer',
    entity: 'MaeilMailUserAnswer',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailUserAnswer.java',
    summary: '회원 매일메일 답변',
    x: 1480,
    y: 1696,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      {
        name: 'issueHistoryId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'maeil_mail_issue_history.id',
      },
      {
        name: 'memberId',
        type: 'Long',
        badges: ['FK', 'NN'],
        relation: 'member.id',
      },
      { name: 'submittedAnswer', type: 'String', badges: ['NN'] },
    ],
    uniques: ['issueHistoryId + memberId'],
  },
  {
    id: 'maeil_mail_issue_job',
    table: 'maeil_mail_issue_job',
    entity: 'MaeilMailIssueJob',
    domain: 'native',
    source: 'nativenewsletter/maeilmail/domain/MaeilMailIssueJob.java',
    summary: '매일메일 발행 배치 진행 상태',
    x: 1760,
    y: 1696,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'issueDate', type: 'LocalDate', badges: ['NN', 'UQ'] },
      {
        name: 'status',
        type: 'MaeilMailIssueJobStatus',
        badges: ['ENUM', 'NN'],
      },
      {
        name: 'lastProcessedTrackId',
        type: 'Long',
        badges: ['FK'],
        relation: 'maeil_mail_subscription_track.id',
      },
    ],
  },
  {
    id: 'notice',
    table: 'notice',
    entity: 'Notice',
    domain: 'ops',
    source: 'notice/domain/Notice.java',
    summary: '공지사항',
    x: 80,
    y: 1912,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'title', type: 'String', badges: ['NN'] },
      { name: 'category', type: 'NoticeCategory', badges: ['ENUM', 'NN'] },
      { name: 'content', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'holiday',
    table: 'holiday',
    entity: 'Holiday',
    domain: 'ops',
    source: 'common/holiday/domain/Holiday.java',
    summary: '휴일 캘린더',
    x: 360,
    y: 1912,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'date', type: 'LocalDate', badges: ['NN', 'UQ'] },
      { name: 'name', type: 'String', badges: ['NN'] },
    ],
  },
  {
    id: 'unsubscribe_pattern',
    table: 'unsubscribe_pattern',
    entity: 'UnsubscribePattern',
    domain: 'ops',
    source: 'subscribe/domain/UnsubscribePattern.java',
    summary: '구독 해지 패턴 룰',
    x: 640,
    y: 1912,
    fields: [
      { name: 'id', type: 'Long', badges: ['PK'] },
      { name: 'pattern', type: 'String', badges: ['NN'] },
      { name: 'description', type: 'String' },
    ],
  },
];

export const erdIndexesByTable: Record<string, ErdIndex[]> = {
  article: [
    {
      name: 'idx_article_member_arrived_id',
      type: 'index',
      columns: ['member_id', 'arrived_date_time DESC', 'id'],
      source: 'V3.10__add_article_member_arrived_idx.sql',
    },
    {
      name: 'idx_article_member_newsletter',
      type: 'index',
      columns: ['member_id', 'newsletter_id'],
      source: 'V5.1.0__add_article_newsletter_index.sql',
    },
  ],
  article_read_history: [
    {
      name: 'uk_article_read_history_member_article',
      type: 'unique',
      columns: ['member_id', 'article_id'],
      source: 'V35.0.0__create_article_read_history_table.sql',
    },
  ],
  blog_category: [
    {
      name: 'uk_blog_category_name',
      type: 'unique',
      columns: ['name'],
      source: 'V31.0.0__create_blog_tables.sql',
    },
  ],
  blog_hashtag: [
    {
      name: 'uk_blog_hashtag_name',
      type: 'unique',
      columns: ['name'],
      source: 'V31.0.0__create_blog_tables.sql',
    },
  ],
  blog_image_asset: [
    {
      name: 'idx_blog_image_asset_blog_post_status',
      type: 'index',
      columns: ['blog_post_id', 'status'],
      source: 'V31.0.0__create_blog_tables.sql',
    },
    {
      name: 'uk_blog_image_asset_object_key',
      type: 'unique',
      columns: ['object_key'],
      source: 'V31.0.0__create_blog_tables.sql',
    },
  ],
  blog_post_tag: [
    {
      name: 'uk_blog_post_tag_post_tag',
      type: 'unique',
      columns: ['blog_post_id', 'blog_hashtag_id'],
      source: 'V31.0.0__create_blog_tables.sql',
    },
  ],
  bookmark: [
    {
      name: 'UKn4tjskat1leb7wje1gaeg3gio',
      type: 'unique',
      columns: ['member_id', 'article_id'],
      source: 'V1__init.sql',
    },
  ],
  challenge_daily_guide: [
    {
      name: 'uk_challenge_daily_guide',
      type: 'unique',
      columns: ['challenge_id', 'day_index'],
      source: 'V17.0.0__create_challenge_daily_guide_tables.sql',
    },
  ],
  challenge_daily_guide_comment: [
    {
      name: 'uk_challenge_daily_guide_comment',
      type: 'unique',
      columns: ['guide_id', 'participant_id'],
      source: 'V17.0.0__create_challenge_daily_guide_tables.sql',
    },
  ],
  challenge_daily_result: [
    {
      name: 'uk_challenge_daily_result',
      type: 'unique',
      columns: ['participant_id', 'date'],
      source: 'V16__create_challenge_tables.sql',
    },
  ],
  challenge_daily_todo: [
    {
      name: 'uk_challenge_daily_todo',
      type: 'unique',
      columns: ['participant_id', 'todo_date', 'challenge_todo_id'],
      source: 'V16__create_challenge_tables.sql',
    },
  ],
  challenge_participant: [
    {
      name: 'uk_challenge_participant',
      type: 'unique',
      columns: ['challenge_id', 'member_id'],
      source: 'V16__create_challenge_tables.sql',
    },
  ],
  challenge_review: [
    {
      name: 'uk_challenge_review_challenge_id_member_id',
      type: 'unique',
      columns: ['challenge_id', 'member_id'],
      source: 'V34.0.0__create_challenge_review_table.sql',
    },
  ],
  challenge_start_notification: [
    {
      name: 'uk_challenge_start_notification_challenge_member',
      type: 'unique',
      columns: ['challenge_id', 'member_id'],
      source: 'V28.0.0__create_challenge_start_notification_table.sql',
    },
  ],
  challenge_todo: [
    {
      name: 'uk_challenge_todo',
      type: 'unique',
      columns: ['challenge_id', 'todo_type'],
      source: 'V16__create_challenge_tables.sql',
    },
  ],
  continue_reading_rank_history: [
    {
      name: 'uk_continue_reading_rank_history_member_period',
      type: 'unique',
      columns: ['member_id', 'period'],
      source: 'V36.0.0__create_reading_rank_history_tables.sql',
    },
  ],
  continue_reading_realtime: [
    {
      name: 'UK1vroxowpjl9xad0kus652hwfx',
      type: 'unique',
      columns: ['member_id'],
      source: 'V1__init.sql',
    },
  ],
  continue_reading_shield: [
    {
      name: 'uk_continue_reading_shield_member_id',
      type: 'unique',
      columns: ['member_id'],
      source: 'V37.0.0__create_continue_reading_shield_tables.sql',
    },
  ],
  continue_reading_shield_history: [
    {
      name: 'uk_continue_reading_shield_history_member_type_reason_date',
      type: 'unique',
      columns: ['member_id', 'type', 'reason', 'event_date'],
      source: 'V37.0.0__create_continue_reading_shield_tables.sql',
    },
  ],
  continue_reading_snapshot: [
    {
      name: 'uk_continue_reading_snapshot_member_id',
      type: 'unique',
      columns: ['member_id'],
      source: 'V31.5.0__create_continue_reading_snapshot_table.sql',
    },
  ],
  coupon_issue: [
    {
      name: 'uk_member_coupon',
      type: 'unique',
      columns: ['member_id', 'coupon_name'],
      source: 'V27.0.0__create_coupon_issue_table.sql',
    },
  ],
  holiday: [
    {
      name: 'uk_holiday_date',
      type: 'unique',
      columns: ['date'],
      source: 'V33.3.0__create_holiday_table.sql',
    },
  ],
  maeil_mail_content_answer: [
    {
      name: 'uk_maeil_mail_content_answer_content_id',
      type: 'unique',
      columns: ['content_id'],
      source: 'V33.1.0__create_maeil_mail_answer_tables.sql',
    },
  ],
  maeil_mail_issue_history: [
    {
      name: 'uk_maeil_mail_issue_history_article_id',
      type: 'unique',
      columns: ['article_id'],
      source: 'V33.0.0__create_maeil_mail_issue_tables.sql',
    },
  ],
  maeil_mail_issue_job: [
    {
      name: 'uk_maeil_mail_issue_job_issue_date',
      type: 'unique',
      columns: ['issue_date'],
      source: 'V33.0.0__create_maeil_mail_issue_tables.sql',
    },
  ],
  maeil_mail_sent_content: [
    {
      name: 'uk_maeil_mail_sent_content',
      type: 'unique',
      columns: ['member_id', 'topic_id', 'content_id'],
      source: 'V33.0.0__create_maeil_mail_issue_tables.sql',
    },
  ],
  maeil_mail_subscription_track: [
    {
      name: 'uk_maeil_mail_subscription_track_subscribe_id_field',
      type: 'unique',
      columns: ['subscribe_id', 'field'],
      source: 'V32.0.0__create_maeil_mail_subscription_tables.sql',
    },
  ],
  maeil_mail_topic: [
    {
      name: 'uk_maeil_mail_topic_track_name',
      type: 'unique',
      columns: ['track', 'name'],
      source: 'V33.0.0__create_maeil_mail_issue_tables.sql',
    },
    {
      name: 'uk_maeil_mail_topic_track_order',
      type: 'unique',
      columns: ['track', 'display_order'],
      source: 'V33.0.0__create_maeil_mail_issue_tables.sql',
    },
  ],
  maeil_mail_user_answer: [
    {
      name: 'uk_maeil_mail_user_answer_member_issue_history',
      type: 'unique',
      columns: ['member_id', 'issue_history_id'],
      source: 'V33.1.0__create_maeil_mail_answer_tables.sql',
    },
  ],
  member: [
    {
      name: 'UKgyl5t9mxo4q408lurfmndln0b',
      type: 'unique',
      columns: ['provider', 'provider_id'],
      source: 'V1__init.sql',
    },
    {
      name: 'UKhh9kg6jti4n1eoiertn2k6qsc',
      type: 'unique',
      columns: ['nickname'],
      source: 'V1__init.sql',
    },
  ],
  monthly_reading_rank_history: [
    {
      name: 'uk_monthly_reading_rank_history_member_period',
      type: 'unique',
      columns: ['member_id', 'period'],
      source: 'V36.0.0__create_reading_rank_history_tables.sql',
    },
  ],
  monthly_reading_realtime: [
    {
      name: 'uk_monthly_reading_realtime_member_id',
      type: 'unique',
      columns: ['member_id'],
      source: 'V6.0.0__create_monthly_reading_realtime_table.sql',
    },
  ],
  monthly_reading_snapshot: [
    {
      name: 'UKivdb27aujnw4u223qhbhwonmn',
      type: 'unique',
      columns: ['member_id'],
      source: 'V1__init.sql',
    },
  ],
  newsletter: [
    {
      name: 'idx_newsletter_category_id',
      type: 'index',
      columns: ['category_id', 'id'],
      source: 'V5.1.0__add_article_newsletter_index.sql',
    },
    {
      name: 'uk_newsletter_detail_id',
      type: 'unique',
      columns: ['detail_id'],
      source: 'V7.4.0__add_newsletter_id_unique_constraint.sql',
    },
  ],
  newsletter_group_item: [
    {
      name: 'uk_newsletter_group_item',
      type: 'unique',
      columns: ['newsletter_group_id', 'newsletter_id'],
      source: 'V23.0.0__create_newsletter_group_structure.sql',
    },
  ],
  newsletter_previous_policy: [
    {
      name: 'uk_newsletter_previous_article_policy_newsletter_id',
      type: 'unique',
      columns: ['newsletter_id'],
      source:
        'V10.0.0__create_previous_article_newsletter_previous_article_table.sql',
    },
  ],
  newsletter_subscription_count: [
    {
      name: 'uk_newsletter_subscription_count_newsletter_id',
      type: 'unique',
      columns: ['newsletter_id'],
      source: 'V7.0.0__add_subscription_count_table.sql',
    },
  ],
  notice: [
    {
      name: 'idx_notice_created_at',
      type: 'index',
      columns: ['created_at'],
      source: 'V14.0.0__create_notice_tables.sql',
    },
  ],
  recent_article: [
    {
      name: 'idx_recent_article_member_arrived',
      type: 'index',
      columns: ['member_id', 'arrived_date_time'],
      source: 'V12.0.0__create_recent_article_table_with_ngram_index.sql',
    },
    {
      name: 'idx_recent_article_newsletter',
      type: 'index',
      columns: ['newsletter_id'],
      source: 'V12.0.0__create_recent_article_table_with_ngram_index.sql',
    },
    {
      name: 'idx_recent_article_contents_text_and_title_ngram',
      type: 'fulltext',
      columns: ['title', 'contents_text'],
      source: 'V12.0.0__create_recent_article_table_with_ngram_index.sql',
    },
  ],
  subscribe: [
    {
      name: 'uk_subscribe_member_newsletter',
      type: 'unique',
      columns: ['member_id', 'newsletter_id'],
      source: 'V33.3.3__add_unique_constraint_to_subscribe.sql',
    },
  ],
  today_reading: [
    {
      name: 'UKcvjl8m00v958hvtkjmr14owr2',
      type: 'unique',
      columns: ['member_id'],
      source: 'V1__init.sql',
    },
  ],
  unsubscribe_pattern: [
    {
      name: 'uk_unsubscribe_pattern_key',
      type: 'unique',
      columns: ['pattern_key'],
      source: 'V29.0.0__create_subscribe_retry_table_unsubscribe_pattern.sql',
    },
  ],
  unsubscribe_retry: [
    {
      name: 'uk_unsubscribe_retry_subscribe_id',
      type: 'unique',
      columns: ['subscribe_id'],
      source: 'V29.0.0__create_subscribe_retry_table_unsubscribe_pattern.sql',
    },
  ],
  warning_setting: [
    {
      name: 'uk_warning_setting_member_id',
      type: 'unique',
      columns: ['member_id'],
      source: 'V13.0.0__create_warning_setting_table.sql',
    },
  ],
  weekly_reading: [
    {
      name: 'UKhrfngbshlk3ve70h08aw61k9r',
      type: 'unique',
      columns: ['member_id'],
      source: 'V1__init.sql',
    },
  ],
  yearly_reading: [
    {
      name: 'unique_member_id_year',
      type: 'unique',
      columns: ['member_id', 'reading_year'],
      source: 'V1__init.sql',
    },
  ],
};

export const erdIndexTotal = Object.values(erdIndexesByTable).reduce(
  (total, indexes) => total + indexes.length,
  0,
);

export const erdRelationships: ErdRelationship[] = [
  {
    from: 'member',
    to: 'role',
    field: 'roleId',
    label: 'role_id',
    strength: 'inferred',
  },
  {
    from: 'warning_setting',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'withdrawn_member',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'coupon_issue',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'pet',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'pet',
    to: 'stage',
    field: 'stageId',
    label: 'stage_id',
    strength: 'inferred',
  },
  {
    from: 'badge',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'newsletter',
    to: 'category',
    field: 'categoryId',
    label: 'category_id',
    strength: 'inferred',
  },
  {
    from: 'newsletter',
    to: 'newsletter_detail',
    field: 'detailId',
    label: 'detail_id',
    strength: 'constraint',
  },
  {
    from: 'newsletter_previous_policy',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'inferred',
  },
  {
    from: 'newsletter_group_item',
    to: 'newsletter_group',
    field: 'newsletterGroupId',
    label: 'newsletter_group_id',
    strength: 'constraint',
  },
  {
    from: 'newsletter_group_item',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'constraint',
  },
  {
    from: 'subscribe',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'constraint',
  },
  {
    from: 'subscribe',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'unsubscribe_retry',
    to: 'subscribe',
    field: 'subscribeId',
    label: 'subscribe_id',
    strength: 'inferred',
  },
  {
    from: 'article',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'article',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'inferred',
  },
  {
    from: 'bookmark',
    to: 'article',
    field: 'articleId',
    label: 'article_id',
    strength: 'constraint',
  },
  {
    from: 'bookmark',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'highlight',
    to: 'article',
    field: 'articleId',
    label: 'article_id',
    strength: 'inferred',
  },
  {
    from: 'highlight',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'highlight',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'inferred',
  },
  {
    from: 'article_read_history',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'article_read_history',
    to: 'article',
    field: 'articleId',
    label: 'article_id',
    strength: 'constraint',
  },
  {
    from: 'article_read_history',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'inferred',
  },
  {
    from: 'article_read_history',
    to: 'category',
    field: 'categoryId',
    label: 'category_id',
    strength: 'inferred',
  },
  {
    from: 'recent_article',
    to: 'article',
    field: 'articleId',
    label: 'article_id',
    strength: 'inferred',
  },
  {
    from: 'recent_article',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'recent_article',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'inferred',
  },
  {
    from: 'previous_article',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'inferred',
  },
  {
    from: 'today_reading',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'weekly_reading',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'monthly_reading_realtime',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'yearly_reading',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'continue_reading_realtime',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'continue_reading_shield',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'continue_reading_shield_history',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'monthly_reading_snapshot',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'continue_reading_snapshot',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'monthly_reading_rank_history',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'continue_reading_rank_history',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'challenge',
    to: 'newsletter_group',
    field: 'newsletterGroupId',
    label: 'newsletter_group_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_team',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_participant',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_participant',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_participant',
    to: 'challenge_team',
    field: 'challengeTeamId',
    label: 'challenge_team_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_todo',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_daily_todo',
    to: 'challenge_participant',
    field: 'participantId',
    label: 'participant_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_daily_todo',
    to: 'challenge_todo',
    field: 'challengeTodoId',
    label: 'challenge_todo_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_daily_result',
    to: 'challenge_participant',
    field: 'participantId',
    label: 'participant_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_daily_guide',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_daily_guide_comment',
    to: 'challenge_daily_guide',
    field: 'guideId',
    label: 'guide_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_daily_guide_comment',
    to: 'challenge_participant',
    field: 'participantId',
    label: 'participant_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_comment',
    to: 'newsletter',
    field: 'newsletterId',
    label: 'newsletter_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_comment',
    to: 'challenge_participant',
    field: 'participantId',
    label: 'participant_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_comment_reply',
    to: 'challenge_comment',
    field: 'commentId',
    label: 'comment_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_comment_reply',
    to: 'challenge_participant',
    field: 'participantId',
    label: 'participant_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_comment_like',
    to: 'challenge_comment',
    field: 'commentId',
    label: 'comment_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_comment_like',
    to: 'challenge_participant',
    field: 'participantId',
    label: 'participant_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_review',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_review',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'challenge_start_notification',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_start_notification',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_todo_reminder_notification',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_todo_reminder_notification',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'inferred',
  },
  {
    from: 'challenge_badge',
    to: 'challenge',
    field: 'challengeId',
    label: 'challenge_id',
    strength: 'inferred',
  },
  {
    from: 'blog_post',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'inferred',
  },
  {
    from: 'blog_post',
    to: 'blog_category',
    field: 'categoryId',
    label: 'category_id',
    strength: 'inferred',
  },
  {
    from: 'blog_post',
    to: 'blog_image_asset',
    field: 'thumbnailImageId',
    label: 'thumbnail_image_id',
    strength: 'inferred',
  },
  {
    from: 'blog_image_asset',
    to: 'blog_post',
    field: 'blogPostId',
    label: 'blog_post_id',
    strength: 'inferred',
  },
  {
    from: 'blog_post_tag',
    to: 'blog_post',
    field: 'blogPostId',
    label: 'blog_post_id',
    strength: 'constraint',
  },
  {
    from: 'blog_post_tag',
    to: 'blog_hashtag',
    field: 'blogHashtagId',
    label: 'blog_hashtag_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_content',
    to: 'maeil_mail_topic',
    field: 'topicId',
    label: 'topic_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_content_answer',
    to: 'maeil_mail_content',
    field: 'contentId',
    label: 'content_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_issue_history',
    to: 'article',
    field: 'articleId',
    label: 'article_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_issue_history',
    to: 'maeil_mail_content',
    field: 'contentId',
    label: 'content_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_sent_content',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_sent_content',
    to: 'maeil_mail_topic',
    field: 'topicId',
    label: 'topic_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_sent_content',
    to: 'maeil_mail_content',
    field: 'contentId',
    label: 'content_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_subscription_track',
    to: 'subscribe',
    field: 'subscribeId',
    label: 'subscribe_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_subscription_track',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_user_answer',
    to: 'maeil_mail_issue_history',
    field: 'issueHistoryId',
    label: 'issue_history_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_user_answer',
    to: 'member',
    field: 'memberId',
    label: 'member_id',
    strength: 'constraint',
  },
  {
    from: 'maeil_mail_issue_job',
    to: 'maeil_mail_subscription_track',
    field: 'lastProcessedTrackId',
    label: 'last_processed_track_id',
    strength: 'inferred',
  },
];
